import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "@fontsource/jetbrains-mono/400.css";

const terminalLines = [
  { text: "> Pasting invoice data...", delay: 0 },
  { text: "> Detecting schema... 3 fields found", delay: 1200 },
  { text: "> Created table: Invoices", delay: 2400 },
  { text: '> Suggested automation: "Alert when overdue"', delay: 3600 },
  { text: "> Done. 2.4 seconds.", delay: 4800 },
];

export const HeroQuiet = () => {
  const navigate = useNavigate();
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [currentText, setCurrentText] = useState<string>("");
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    if (visibleLines >= terminalLines.length) {
      // Reset after a pause
      const resetTimeout = setTimeout(() => {
        setVisibleLines(0);
        setCurrentText("");
        setIsTyping(true);
      }, 3000);
      return () => clearTimeout(resetTimeout);
    }

    const line = terminalLines[visibleLines];
    let charIndex = 0;

    const typeChar = () => {
      if (charIndex < line.text.length) {
        setCurrentText(line.text.slice(0, charIndex + 1));
        charIndex++;
        setTimeout(typeChar, 25 + Math.random() * 20);
      } else {
        setIsTyping(false);
        setTimeout(() => {
          setVisibleLines((prev) => prev + 1);
          setCurrentText("");
          setIsTyping(true);
        }, 400);
      }
    };

    const startTimeout = setTimeout(typeChar, visibleLines === 0 ? 500 : 100);
    return () => clearTimeout(startTimeout);
  }, [visibleLines]);

  return (
    <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 gradient-hero gradient-mesh -z-10" />
      
      <div className="container mx-auto px-4 py-12 sm:py-16 md:py-24">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 md:gap-12 lg:gap-20 items-center">
          {/* Left: Copy */}
          <div className="space-y-6 sm:space-y-8 animate-slide-up text-center lg:text-left">
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] tracking-tight">
                Chaos in.
                <br />
                <span className="text-gradient">Clarity out.</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg mx-auto lg:mx-0 pt-2 sm:pt-4">
                Drop any data. Watch it organize itself.
                <span className="hidden sm:inline"><br /></span>{" "}
                Your AI structures tables, writes automations,
                <span className="hidden sm:inline"><br /></span>{" "}
                and keeps everything running while you sleep.
              </p>
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-2">
              <Button 
                size="lg" 
                className="h-12 sm:h-12 px-6 sm:px-8 text-base font-medium group w-full sm:w-auto touch-target"
                onClick={() => navigate("/auth")}
              >
                See it work
                <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Button>
              <Button 
                variant="ghost" 
                size="lg"
                className="h-12 px-6 text-base font-medium text-muted-foreground hover:text-foreground w-full sm:w-auto touch-target"
                onClick={() => navigate("/auth")}
              >
                Start building →
              </Button>
            </div>
            
            {/* Micro-text footer */}
            <p className="text-xs text-muted-foreground font-mono pt-2 sm:pt-4 tracking-wide">
              Last schema detected: 4 minutes ago · 12,847 tables created this week
            </p>
          </div>
          
          {/* Right: Terminal */}
          <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/5 rounded-2xl blur-2xl opacity-60" />
            <div className="relative bg-terminal rounded-xl border border-border overflow-hidden shadow-xl">
              {/* Terminal header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-terminal-header">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-destructive/80" />
                  <div className="w-3 h-3 rounded-full bg-accent/80" />
                  <div className="w-3 h-3 rounded-full bg-terminal-text/80" />
                </div>
                <span className="text-xs text-muted-foreground font-mono ml-2">strata-agent</span>
              </div>
              
              {/* Terminal body */}
              <div className="p-5 min-h-[240px] font-mono text-sm">
                {/* Completed lines */}
                {terminalLines.slice(0, visibleLines).map((line, idx) => (
                  <div key={idx} className="text-terminal-text mb-2 leading-relaxed">
                    {line.text}
                  </div>
                ))}
                
                {/* Currently typing line */}
                {visibleLines < terminalLines.length && (
                  <div className="text-terminal-text leading-relaxed">
                    {currentText}
                    <span className={`inline-block w-2 h-4 bg-terminal-text ml-0.5 align-middle ${isTyping ? "animate-pulse" : ""}`} />
                  </div>
                )}
                
                {/* Completed cursor */}
                {visibleLines >= terminalLines.length && (
                  <div className="text-terminal-text mt-4">
                    <span className="inline-block w-2 h-4 bg-terminal-text animate-pulse" />
                  </div>
                )}
              </div>
              
              {/* Scanline overlay */}
              <div className="absolute inset-0 pointer-events-none bg-scanline opacity-[0.02]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
